import type {
  EstimateCard,
  EstimateItemInput,
  EstimateItemResourceRow,
  EstimateItemRow,
  EstimateVersionKind,
  Kenml2ReconcileResult,
  NormSearchResult,
  NormsOverview,
  RepriceReport,
} from '@epc/contracts';
import { ESTIMATE_VERSION_KINDS, PRICE_POSITION_DEFAULTS } from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { hours, sum } from './lib';

type Kind = EstimateItemInput['type'];

/** Число из поля: пробелы и запятая допускаются («1 000,5») */
const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));

const KIND_LABEL: Record<Kind, string> = {
  work: 'Работа по норме ЭСН',
  material: 'Материал из сборника цен',
  equipment: 'Оборудование из сборника',
  price: 'Прайсовая позиция по КП',
  other: 'Прочие затраты',
};

function useNormSearch(q: string, kind: 'items' | 'resources') {
  return useQuery({
    queryKey: ['norms', 'search', q, kind],
    queryFn: () => api<NormSearchResult>('/norms/search', { query: { q, kind, limit: 30 } }),
    enabled: q.trim().length >= 2,
  });
}

/** Новая позиция сметы (СМ-05, СМ-06) */
export function AddItemDialog({
  card,
  afterItemId,
  onClose,
  onSaved,
}: {
  card: EstimateCard;
  afterItemId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState<Kind>('work');
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [normId, setNormId] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [picked, setPicked] = useState<string>('');
  const [f, setF] = useState({
    quantity: '1',
    quantityExpr: '',
    sectionId: card.sections[0]?.id ?? '',
    name: '',
    unit: '',
    code: '',
    priceGross: '',
    priceKind: 'material' as 'material' | 'equipment' | 'other',
    source: '',
    climate: '',
    note: '',
  });
  useEffect(() => {
    const t = setTimeout(() => setQ(typed), 300);
    return () => clearTimeout(t);
  }, [typed]);
  const search = useNormSearch(q, kind === 'work' ? 'items' : 'resources');
  const m = useMutation({
    mutationFn: () => {
      const qty = num(f.quantity);
      const body: EstimateItemInput = {
        type: kind,
        sectionId: f.sectionId || null,
        normItemId: kind === 'work' ? normId : undefined,
        resourceId: kind === 'material' || kind === 'equipment' ? resourceId : undefined,
        name: f.name.trim() || undefined,
        unit: f.unit.trim() || undefined,
        code: f.code.trim() || null,
        quantity: qty,
        quantityExpr: f.quantityExpr.trim() || null,
        priceGross:
          kind === 'price' || kind === 'other'
            ? Number(f.priceGross.replace(/\s/g, '').replace(',', '.'))
            : undefined,
        priceKind: kind === 'price' ? f.priceKind : undefined,
        source: f.source.trim() || null,
        coefficients:
          kind === 'work' && f.climate
            ? [
                {
                  code: `П-Г${f.climate}-6`,
                  name: `Климатические условия температурной зоны ${f.climate === '1' ? 'I' : f.climate === '2' ? 'II' : 'III'} — ${f.climate === '1' ? '1,04' : f.climate === '2' ? '1,06' : '1,08'}`,
                  basis: 'ЭСН РК 8.04-01-2024, общие положения, приложение (температурная зона стройки)',
                  kLabor: f.climate === '1' ? 1.04 : f.climate === '2' ? 1.06 : 1.08,
                  kMachines: f.climate === '1' ? 1.04 : f.climate === '2' ? 1.06 : 1.08,
                },
              ]
            : undefined,
        afterItemId,
        note: f.note.trim() || null,
      };
      return api<{ id: string; warnings: string[] }>(`/estimates/${card.id}/items`, { body });
    },
    onSuccess: (r) => {
      toast(
        r.warnings.length ? `Позиция добавлена, но ${r.warnings.join('; ')}` : 'Позиция добавлена',
        r.warnings.length ? 'info' : 'success',
      );
      onSaved();
    },
  });
  const set = (p: Partial<typeof f>) => setF({ ...f, ...p });
  const canSave =
    num(f.quantity) >= 0 &&
    (kind === 'work'
      ? Boolean(normId)
      : kind === 'material' || kind === 'equipment'
        ? Boolean(resourceId)
        : kind === 'price'
          ? Boolean(f.name.trim() && f.priceGross && f.source.trim())
          : Boolean(f.name.trim() && f.priceGross));
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Новая позиция"
      description={afterItemId ? 'Вставится после выбранной позиции' : 'Добавится в конец сметы'}
      width="lg"
      footer={
        <>
          <Button className="min-h-[44px] md:min-h-0" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="min-h-[44px] md:min-h-0"
            variant="primary"
            loading={m.isPending}
            disabled={!canSave}
            onClick={() => m.mutate()}
          >
            Добавить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2" role="tablist">
          {(Object.keys(KIND_LABEL) as Kind[]).map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={kind === k}
              onClick={() => {
                setKind(k);
                setPicked('');
                setNormId('');
                setResourceId('');
              }}
              className={`min-h-[44px] rounded-md px-3 text-[13px] font-medium md:min-h-9 ${kind === k ? 'bg-ink text-on-ink' : 'bg-surface-2 text-ink-2 hover:text-ink'}`}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
        {kind === 'work' || kind === 'material' || kind === 'equipment' ? (
          <>
            <Field
              label={
                kind === 'work' ? 'Норма из базы (код или слова)' : 'Ресурс из сборника цен (код или слова)'
              }
            >
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={kind === 'work' ? '1310-0701, монтаж прибора…' : '216-103, дюбель…'}
                autoFocus
              />
            </Field>
            {picked ? (
              <p className="text-[13px] text-ink">
                Выбрано: <b>{picked}</b>
              </p>
            ) : null}
            {search.isPending && q.trim().length >= 2 ? <Spinner label="Поиск" /> : null}
            {search.data ? (
              <ul className="max-h-60 divide-y divide-line overflow-auto rounded-md border border-line">
                {(kind === 'work'
                  ? search.data.items
                  : search.data.resources.filter((r) =>
                      kind === 'equipment' ? r.kind === 'equipment' : r.kind === 'material',
                    )
                ).map((x) => (
                  <li key={x.id}>
                    <button
                      type="button"
                      className="flex min-h-[44px] w-full flex-col gap-0.5 px-3 py-2 text-left text-[13px] hover:bg-surface-2"
                      onClick={() => {
                        if (kind === 'work') setNormId(x.id);
                        else setResourceId(x.id);
                        setPicked(`${x.code} — ${x.name}`);
                        set({ unit: x.unit });
                      }}
                    >
                      <span className="num text-ink">{x.code}</span>
                      <span className="text-ink-2 [overflow-wrap:anywhere]">{x.name}</span>
                      <span className="text-[12px] text-ink-2">
                        {x.unit} · {x.releaseCode}
                        {'unitDirect' in x && x.unitDirect !== null ? ` · ${sum(x.unitDirect)} ₸` : ''}
                        {'price' in x && x.price !== null ? ` · ${sum(x.price)} ₸` : ''}
                        {'laborHours' in x && x.laborHours ? ` · ${hours(x.laborHours)} чел.-ч` : ''}
                      </span>
                    </button>
                  </li>
                ))}
                {(kind === 'work'
                  ? search.data.items
                  : search.data.resources.filter((r) =>
                      kind === 'equipment' ? r.kind === 'equipment' : r.kind === 'material',
                    )
                ).length === 0 ? (
                  <li className="px-3 py-2 text-[13px] text-ink-2">
                    Ничего не найдено. Нормы из выпусков «требует сверки» не подставляются — подтвердите
                    статус в нормативной базе.
                  </li>
                ) : null}
              </ul>
            ) : null}
          </>
        ) : null}
        {kind === 'price' || kind === 'other' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Наименование" className="sm:col-span-2">
              <Input value={f.name} onChange={(e) => set({ name: e.target.value })} autoFocus />
            </Field>
            <Field label="Код (АГСК-3, если есть)">
              <Input value={f.code} onChange={(e) => set({ code: e.target.value })} />
            </Field>
            <Field label="Единица">
              <Input
                value={f.unit}
                onChange={(e) => set({ unit: e.target.value })}
                placeholder={kind === 'price' ? 'шт.' : 'компл.'}
              />
            </Field>
            {kind === 'price' ? (
              <>
                <Field
                  label="Цена по КП с НДС, ₸"
                  hint={`Цена делится на НДС, умножается на заготовительно-складские расходы и коэффициент приведения К из действующего выпуска индексов (по умолчанию ${String(PRICE_POSITION_DEFAULTS.vat).replace('.', ',')}; ${String(PRICE_POSITION_DEFAULTS.storageMaterials).replace('.', ',')} материалы, ${String(PRICE_POSITION_DEFAULTS.storageEquipment).replace('.', ',')} оборудование; К ${String(PRICE_POSITION_DEFAULTS.index).replace('.', ',')})`}
                >
                  <Input
                    inputMode="decimal"
                    value={f.priceGross}
                    onChange={(e) => set({ priceGross: e.target.value })}
                  />
                </Field>
                <Field label="Что это">
                  <Select
                    value={f.priceKind}
                    onChange={(e) => set({ priceKind: e.target.value as typeof f.priceKind })}
                  >
                    <option value="material">Материал</option>
                    <option value="equipment">Оборудование</option>
                    <option value="other">Прочие (услуги, лицензии)</option>
                  </Select>
                </Field>
                <Field label="Обоснование: номер КП и поставщик" className="sm:col-span-2">
                  <Input
                    value={f.source}
                    onChange={(e) => set({ source: e.target.value })}
                    placeholder="КП № 12 от 10.10.2026, ТОО «Поставщик»"
                  />
                </Field>
              </>
            ) : (
              <Field label="Стоимость единицы, ₸">
                <Input
                  inputMode="decimal"
                  value={f.priceGross}
                  onChange={(e) => set({ priceGross: e.target.value })}
                />
              </Field>
            )}
          </div>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Количество">
            <Input
              inputMode="decimal"
              value={f.quantity}
              onChange={(e) => set({ quantity: e.target.value })}
            />
          </Field>
          <Field label="Формула объёма (как у сметчика)">
            <Input
              value={f.quantityExpr}
              onChange={(e) => set({ quantityExpr: e.target.value })}
              placeholder="5-2, 50.1,02"
            />
          </Field>
          {card.sections.length > 1 ? (
            <Field label="Раздел">
              <Select value={f.sectionId} onChange={(e) => set({ sectionId: e.target.value })}>
                {card.sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.number ? `${s.number}. ` : ''}
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {kind === 'work' ? (
            <Field label="Температурная зона стройки" hint="Коэффициент к труду и машинам">
              <Select value={f.climate} onChange={(e) => set({ climate: e.target.value })}>
                <option value="">без коэффициента</option>
                <option value="1">I — 1,04</option>
                <option value="2">II — 1,06 (Атырауская область)</option>
                <option value="3">III — 1,08</option>
              </Select>
            </Field>
          ) : null}
        </div>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Замена ресурса позиции с обоснованием (СМ-06) */
export function ReplaceResourceDialog({
  estimateId,
  item,
  resource,
  onClose,
  onSaved,
}: {
  estimateId: string;
  item: EstimateItemRow;
  resource: EstimateItemResourceRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [picked, setPicked] = useState('');
  const [f, setF] = useState({
    name: '',
    unit: resource.unit,
    price: '',
    norm: resource.norm !== null ? String(resource.norm) : '',
    reason: '',
  });
  useEffect(() => {
    const t = setTimeout(() => setQ(typed), 300);
    return () => clearTimeout(t);
  }, [typed]);
  const search = useNormSearch(q, 'resources');
  const m = useMutation({
    mutationFn: () =>
      api(`/estimates/${estimateId}/items/${item.id}/resources/${resource.id}/replace`, {
        body: {
          resourceId: resourceId || undefined,
          name: resourceId ? undefined : f.name.trim() || undefined,
          unit: resourceId ? undefined : f.unit.trim() || undefined,
          price: resourceId
            ? undefined
            : f.price
              ? Number(f.price.replace(/\s/g, '').replace(',', '.'))
              : undefined,
          norm: f.norm ? Number(f.norm.replace(',', '.')) : undefined,
          reason: f.reason.trim(),
        },
      }),
    onSuccess: () => {
      toast('Ресурс заменён');
      onSaved();
    },
  });
  const set = (p: Partial<typeof f>) => setF({ ...f, ...p });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Замена ресурса"
      description={`${resource.code} — ${resource.name}; позиция ${item.number}: ${item.name}`}
      footer={
        <>
          <Button className="min-h-[44px] md:min-h-0" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="min-h-[44px] md:min-h-0"
            variant="primary"
            loading={m.isPending}
            disabled={f.reason.trim().length < 3 || (!resourceId && !(f.name.trim() && f.price))}
            onClick={() => m.mutate()}
          >
            Заменить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Ресурс из сборника цен">
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="код или слова" />
        </Field>
        {picked ? (
          <p className="text-[13px]">
            Выбрано: <b>{picked}</b>
          </p>
        ) : null}
        {search.data?.resources.length ? (
          <ul className="max-h-48 divide-y divide-line overflow-auto rounded-md border border-line">
            {search.data.resources.map((x) => (
              <li key={x.id}>
                <button
                  type="button"
                  className="flex min-h-[44px] w-full flex-col px-3 py-2 text-left text-[13px] hover:bg-surface-2"
                  onClick={() => {
                    setResourceId(x.id);
                    setPicked(`${x.code} — ${x.name}`);
                  }}
                >
                  <span className="num">{x.code}</span>
                  <span className="text-ink-2 [overflow-wrap:anywhere]">
                    {x.name} · {x.unit}
                    {x.price !== null ? ` · ${sum(x.price)} ₸` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <p className="text-[13px] text-ink-2">Или ресурс не из базы — наименование и цена вручную:</p>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Наименование" className="sm:col-span-2">
            <Input
              value={f.name}
              onChange={(e) => set({ name: e.target.value })}
              disabled={Boolean(resourceId)}
            />
          </Field>
          <Field label="Цена, ₸">
            <Input
              inputMode="decimal"
              value={f.price}
              onChange={(e) => set({ price: e.target.value })}
              disabled={Boolean(resourceId)}
            />
          </Field>
          <Field label="Единица">
            <Input
              value={f.unit}
              onChange={(e) => set({ unit: e.target.value })}
              disabled={Boolean(resourceId)}
            />
          </Field>
          <Field label="Норма расхода">
            <Input inputMode="decimal" value={f.norm} onChange={(e) => set({ norm: e.target.value })} />
          </Field>
        </div>
        <Field label="Обоснование замены" hint="Печатается в обосновании позиции">
          <Textarea rows={2} value={f.reason} onChange={(e) => set({ reason: e.target.value })} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Коэффициенты позиции: у каждого название и обоснование (СМ-08) */
export function CoefficientsDialog({
  estimateId,
  item,
  onClose,
  onSaved,
}: {
  estimateId: string;
  item: EstimateItemRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  type Row = {
    k: string;
    code: string | null | undefined;
    name: string;
    basis: string;
    kLaborS: string;
    kMachinesS: string;
  };
  const [rows, setRows] = useState<Row[]>(
    item.coefficients
      .filter((c) => c.kind !== 'price')
      .map((c, i) => ({
        k: `r${i}`,
        code: c.code,
        name: c.name ?? '',
        basis: c.basis ?? '',
        kLaborS: c.kLabor !== null ? String(c.kLabor) : '',
        kMachinesS: c.kMachines !== null ? String(c.kMachines) : '',
      })),
  );
  const m = useMutation({
    mutationFn: () =>
      api(`/estimates/${estimateId}/items/${item.id}/coefficients`, {
        method: 'PUT',
        body: {
          coefficients: rows.map((r) => ({
            code: r.code?.trim() || null,
            name: r.name.trim(),
            basis: r.basis.trim(),
            kLabor: r.kLaborS ? Number(r.kLaborS.replace(',', '.')) : null,
            kMachines: r.kMachinesS ? Number(r.kMachinesS.replace(',', '.')) : null,
          })),
        },
      }),
    onSuccess: () => {
      toast('Коэффициенты сохранены');
      onSaved();
    },
  });
  const set = (k: string, p: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.k === k ? { ...r, ...p } : r)));
  const valid = rows.every(
    (r) => r.name.trim().length >= 2 && r.basis.trim().length >= 2 && (r.kLaborS || r.kMachinesS),
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Коэффициенты: позиция ${item.number}`}
      description="Каждый коэффициент — с названием и обоснованием (СМ-08). Коэффициент приведения прайсовых позиций задаётся выпуском индексов."
      width="lg"
      footer={
        <>
          <Button className="min-h-[44px] md:min-h-0" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="min-h-[44px] md:min-h-0"
            variant="primary"
            loading={m.isPending}
            disabled={!valid}
            onClick={() => m.mutate()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {rows.map((r) => (
          <div key={r.k} className="grid gap-2 rounded-md border border-line p-3 sm:grid-cols-6">
            <Field label="Код">
              <Input
                value={r.code ?? ''}
                onChange={(e) => set(r.k, { code: e.target.value })}
                placeholder="П-Г2-6"
              />
            </Field>
            <Field label="Название" className="sm:col-span-3">
              <Input value={r.name} onChange={(e) => set(r.k, { name: e.target.value })} />
            </Field>
            <Field label="К труду">
              <Input
                inputMode="decimal"
                value={r.kLaborS}
                onChange={(e) => set(r.k, { kLaborS: e.target.value })}
              />
            </Field>
            <Field label="К машинам">
              <Input
                inputMode="decimal"
                value={r.kMachinesS}
                onChange={(e) => set(r.k, { kMachinesS: e.target.value })}
              />
            </Field>
            <Field label="Обоснование" className="sm:col-span-5">
              <Input
                value={r.basis}
                onChange={(e) => set(r.k, { basis: e.target.value })}
                placeholder="ЭСН РК 8.04-01-2024, общие положения, п. …"
              />
            </Field>
            <div className="flex items-end">
              <Button
                className="min-h-[44px] md:min-h-0"
                variant="ghost"
                icon={<Trash2 className="size-4" />}
                onClick={() => setRows(rows.filter((x) => x.k !== r.k))}
              >
                Убрать
              </Button>
            </div>
          </div>
        ))}
        <div>
          <Button
            className="min-h-[44px] md:min-h-0"
            variant="secondary"
            icon={<Plus className="size-4" />}
            onClick={() =>
              setRows([
                ...rows,
                { k: `n${Date.now()}`, code: '', name: '', basis: '', kLaborS: '', kMachinesS: '' },
              ])
            }
          >
            Коэффициент
          </Button>
        </div>
        {item.coefficients.some((c) => c.kind === 'price') ? (
          <Badge tone="warning">К приведения прайсовой позиции: {item.priceIndex}</Badge>
        ) : null}
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Пересчёт сметы в новый уровень цен (СМ-02): выбор выпусков сборников цен */
export function RepriceDialog({
  card,
  onClose,
  onDone,
}: {
  card: EstimateCard;
  onClose: () => void;
  onDone: (r: RepriceReport) => void;
}) {
  const ov = useQuery({ queryKey: ['norms'], queryFn: () => api<NormsOverview>('/norms') });
  const [ids, setIds] = useState<string[]>([]);
  const [versionKind, setVersionKind] = useState<EstimateVersionKind>('working');
  const m = useMutation({
    mutationFn: () =>
      api<RepriceReport>(`/estimates/${card.id}/reprice`, { body: { releaseIds: ids, versionKind } }),
    onSuccess: onDone,
  });
  const priceReleases = (ov.data?.releases ?? []).filter(
    (r) => ['ssc', 'scem', 'sczt', 'scpg', 'pricelist'].includes(r.kind) && r.status === 'active',
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Пересчитать в новый уровень цен"
      description="Создаётся новая версия сметы: цены ресурсов и каталожных позиций берутся из выбранных выпусков по коду, нормы и объёмы не меняются. Прежняя версия хранится."
      width="lg"
      footer={
        <>
          <Button className="min-h-[44px] md:min-h-0" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="min-h-[44px] md:min-h-0"
            variant="primary"
            loading={m.isPending}
            disabled={!ids.length}
            onClick={() => m.mutate()}
          >
            Пересчитать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {ov.isPending ? <Spinner /> : null}
        {priceReleases.length ? (
          <ul className="divide-y divide-line rounded-md border border-line">
            {priceReleases.map((r) => (
              <li key={r.id}>
                <label className="flex min-h-[44px] cursor-pointer items-center gap-3 px-3 py-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={ids.includes(r.id)}
                    onChange={(e) =>
                      setIds(e.target.checked ? [...ids, r.id] : ids.filter((x) => x !== r.id))
                    }
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-ink">{r.code}</span>
                    <span className="block text-ink-2">
                      {r.priceZone ? `зона ${r.priceZone} · ` : ''}
                      {r.priceLevel ?? ''} · {r.resources} ресурсов
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        ) : ov.data ? (
          <p className="text-[13px] text-ink-2">
            Нет действующих выпусков сборников цен: подтвердите статус выпусков в нормативной базе.
          </p>
        ) : null}
        <Field label="Вид новой версии">
          <Select value={versionKind} onChange={(e) => setVersionKind(e.target.value as EstimateVersionKind)}>
            {(Object.keys(ESTIMATE_VERSION_KINDS) as EstimateVersionKind[]).map((k) => (
              <option key={k} value={k}>
                {ESTIMATE_VERSION_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Отчёт пересчёта: что изменилось */
export function RepriceReportDialog({ r, onClose }: { r: RepriceReport; onClose: () => void }) {
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Смета пересчитана"
      description={`Было ${tenge(r.totalBefore)}, стало ${tenge(r.totalAfter)}; изменилось позиций: ${r.changed.length}, без изменений: ${r.unchanged}`}
      width="lg"
      footer={
        <Button className="min-h-[44px] md:min-h-0" variant="primary" onClick={onClose}>
          Открыть новую версию
        </Button>
      }
    >
      {r.changed.length ? (
        <ul className="max-h-72 divide-y divide-line overflow-auto text-[13px]">
          {r.changed.map((c) => (
            <li key={c.itemNumber} className="flex items-center gap-3 py-2">
              <span className="num w-8 text-ink-2">{c.itemNumber}</span>
              <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{c.name}</span>
              <span className="num text-ink-2">
                {sum(c.before)} → {sum(c.after)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-2">Цены всех ресурсов совпали с выбранными выпусками.</p>
      )}
      {r.missing.length ? (
        <div className="mt-3 rounded-md bg-warning-soft p-3 text-[13px] text-warning">
          <p className="font-medium">Ресурсов нет в выбранных выпусках, их цены оставлены прежними:</p>
          <ul className="mt-1 list-disc pl-5">
            {r.missing.slice(0, 15).map((x) => (
              <li key={x.code}>
                {x.code} — {x.name}
              </li>
            ))}
            {r.missing.length > 15 ? <li>… и ещё {r.missing.length - 15}</li> : null}
          </ul>
        </div>
      ) : null}
    </Dialog>
  );
}

/** Выгрузка KENML 2.01 для госэкспертизы (СМ-14): реквизиты и формальная проверка перед скачиванием */
export function Kenml2Dialog({ card, onClose }: { card: EstimateCard; onClose: () => void }) {
  const [compiler, setCompiler] = useState('');
  const [compilerPosition, setCompilerPosition] = useState('Инженер-сметчик');
  const [inspector, setInspector] = useState('');
  const [inspectorPosition, setInspectorPosition] = useState('Руководитель сметного отдела');
  const [tdoc, setTdoc] = useState<'1' | '2'>('1');
  const [equipmentKind, setEquipmentKind] = useState<'' | '1' | '2'>('');
  const query = {
    compiler: compiler || undefined,
    compilerPosition,
    inspector: inspector || undefined,
    inspectorPosition,
    tdoc,
    equipmentKind: equipmentKind || undefined,
  };
  // Предупреждения зависят только от того, заполнены ли ФИО и вид оборудования: не дёргаем сервер на каждую букву
  const check = useQuery({
    queryKey: ['estimate', card.id, 'kenml2-check', Boolean(compiler), Boolean(inspector), equipmentKind],
    queryFn: () =>
      api<{ warnings: string[]; compiler: string; inspector: string }>(`/estimates/${card.id}/kenml2-check`, {
        query,
      }),
  });
  const qs = new URLSearchParams(
    Object.entries({ format: 'kenml2', ...query }).filter(([, v]) => v) as Array<[string, string]>,
  ).toString();
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="KENML 2.01 для госэкспертизы"
      description="Файл по СП РК 1.02-123-2025 (таблица Б.1) подаётся вместе с PDF той же сметы. Эталона формата у экспертизы нет: перед подачей файл проверяется на портале экспертизы или в АВС-KZ."
      width="lg"
      footer={
        <>
          <Button className="min-h-[44px] md:min-h-0" variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
          <a
            href={`/api/v1/estimates/${card.id}/export?${qs}`}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-accent px-4 text-[14px] font-semibold text-on-accent hover:underline md:min-h-10"
          >
            Скачать KENML 2.01
          </a>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Составил (ФИО)"
            hint={check.data?.compiler ? `пусто — ${check.data.compiler}` : undefined}
          >
            <Input value={compiler} onChange={(e) => setCompiler(e.target.value)} />
          </Field>
          <Field label="Должность">
            <Input value={compilerPosition} onChange={(e) => setCompilerPosition(e.target.value)} />
          </Field>
          <Field
            label="Проверил (ФИО)"
            hint={check.data?.inspector ? `пусто — ${check.data.inspector}` : 'кто утвердил смету'}
          >
            <Input value={inspector} onChange={(e) => setInspector(e.target.value)} />
          </Field>
          <Field label="Должность">
            <Input value={inspectorPosition} onChange={(e) => setInspectorPosition(e.target.value)} />
          </Field>
          <Field label="Тип документа">
            <Select value={tdoc} onChange={(e) => setTdoc(e.target.value as '1' | '2')}>
              <option value="1">1 — смета инвестора</option>
              <option value="2">2 — смета подрядчика (оферта)</option>
            </Select>
          </Field>
          <Field label="Вид оборудования" hint="робот экспертизы требует пометку у оборудования">
            <Select
              value={equipmentKind}
              onChange={(e) => setEquipmentKind(e.target.value as '' | '1' | '2')}
            >
              <option value="">не указан</option>
              <option value="1">1 — инженерное</option>
              <option value="2">2 — технологическое</option>
            </Select>
          </Field>
        </div>
        {check.isPending ? (
          <Spinner />
        ) : check.isError ? (
          <ErrorBox error={check.error} />
        ) : check.data.warnings.length ? (
          <div className="rounded-md bg-warning-soft px-4 py-3 text-[13px]">
            <p className="font-medium">Формальная проверка: что не понравится роботу экспертизы</p>
            <ul className="mt-1 list-disc pl-5">
              {check.data.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-[13px] text-success">
            Формальная проверка пройдена: обязательные поля заполнены.
          </p>
        )}
        <Kenml2Reconcile estimateId={card.id} />
      </div>
    </Dialog>
  );
}

/** Сверка файла KENML 2.01 (от проектировщика или нашей выгрузки) со сметой до тенге (СМ-13, ПМ-02) */
function Kenml2Reconcile({ estimateId }: { estimateId: string }) {
  const [result, setResult] = useState<Kenml2ReconcileResult | null>(null);
  const run = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api<Kenml2ReconcileResult>(`/estimates/${estimateId}/kenml2-reconcile`, {
        method: 'POST',
        form,
      });
    },
    onSuccess: setResult,
  });
  const rows = result ? [...result.totals, ...result.items] : [];
  return (
    <div className="flex flex-col gap-2 border-t border-line pt-3">
      <p className="text-[13px] font-medium">Сверить с файлом 2.01</p>
      <p className="text-[12px] text-muted">
        Файл не загружается как смета: портал только показывает расхождения с этой сметой — итоги и каждую
        позицию по номеру, до тенге.
      </p>
      <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 self-start rounded-md border border-line px-4 text-[14px] font-medium text-ink hover:bg-surface-2 md:min-h-10">
        {run.isPending ? 'Сверяю…' : 'Выбрать файл .xml'}
        <input
          type="file"
          accept=".xml,.kenml"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) run.mutate(f);
          }}
        />
      </label>
      {run.isError ? <ErrorBox error={run.error} /> : null}
      {result ? (
        <div className="flex flex-col gap-2 text-[13px]">
          <p className={result.ok ? 'text-success' : 'font-medium text-danger'}>
            {result.ok
              ? `Расхождений нет: ${result.matched} из ${result.fileItems} позиций файла совпали с сметой, итоги равны.`
              : `Есть расхождения: сверено позиций ${result.matched} из ${result.fileItems}.`}
          </p>
          {rows.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[12px]">
                <thead className="text-muted">
                  <tr>
                    <th className="py-1 pr-3 font-medium">Что</th>
                    <th className="py-1 pr-3 text-right font-medium">Файл</th>
                    <th className="py-1 pr-3 text-right font-medium">Портал</th>
                    <th className="py-1 text-right font-medium">Разница</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 50).map((r) => (
                    <tr key={`${r.what}|${r.file}|${r.portal}`} className="border-t border-line">
                      <td className="py-1 pr-3">{r.what}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{r.file ?? '—'}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{r.portal ?? '—'}</td>
                      <td className="py-1 text-right tabular-nums">{r.diff ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 50 ? <p className="text-muted">… и ещё {rows.length - 50}</p> : null}
            </div>
          ) : null}
          {result.onlyInFile.length ? (
            <p>Позиции только в файле: {result.onlyInFile.slice(0, 30).join(', ')}</p>
          ) : null}
          {result.onlyInPortal.length ? (
            <p>Позиции только в смете портала: {result.onlyInPortal.slice(0, 30).join(', ')}</p>
          ) : null}
          {result.codeMismatch.length ? (
            <ul className="list-disc pl-5">
              {result.codeMismatch.slice(0, 10).map((m) => (
                <li key={m}>Другой шифр: {m}</li>
              ))}
            </ul>
          ) : null}
          {result.warnings.length ? (
            <div className="rounded-md bg-warning-soft px-3 py-2">
              <p className="font-medium">Формальные замечания к файлу</p>
              <ul className="mt-1 list-disc pl-5">
                {result.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
